import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  UserNotification,
  NotificationPreferences,
  NotificationQueryParams,
  CreateNotificationRequest,
  ScheduleNotificationRequest,
  UpdatePreferencesRequest,
  NotificationListResponse,
  UnreadCountResponse,
  NotificationActionResponse,
  PreferencesResponse,
  NotificationHistoryItem,
  NotificationHistoryResponse,
  NotificationRecipientsResponse,
  RecipientStatus,
  PushSubscriberListResponse,
  SystemNotificationModule,
  SystemNotificationConfigItem,
  SystemNotificationConfigList,
  UpsertSystemNotificationConfigRequest,
} from '@/types/notification.types';

/**
 * Pull the most useful human-readable error message out of a backend
 * response or axios error payload. Different paths produce different
 * envelope shapes:
 *
 *   Validation / business errors (ErrorEntity):
 *     { success: false, code: 400, error: { error_code, error_description } }
 *
 *   Generic NestJS errors / pipes / guards:
 *     { statusCode, message, error }
 *
 *   Result-style envelopes:
 *     { success: false, description: '...' }
 *
 * The fallback chain tries each, returning the first defined non-empty
 * string. Callers OR this against a hardcoded fallback string.
 */
function extractErrorMessage(payload: any): string | undefined {
  if (!payload) return undefined;
  return (
    payload?.error?.error_description ||
    payload?.error?.message ||
    payload?.message ||
    payload?.description ||
    (typeof payload?.error === 'string' ? payload.error : undefined)
  );
}

class NotificationService {
  // ============== USER ENDPOINTS ==============

  /**
   * Get paginated list of user's notifications
   */
  async getNotifications(params: NotificationQueryParams & { exclude_cleared?: boolean; is_read?: boolean; channel?: string } = {}): Promise<NotificationListResponse> {
    try {
      // POST + JSON body — booleans arrive as booleans (no query coercion).
      const body: any = {
        page: params.skip != null ? Math.floor(params.skip / (params.limit || 20)) + 1 : 1,
        limit: params.limit || 20,
        type: params.type,
        is_read: params.is_read !== undefined
          ? params.is_read
          : params.status === 'READ' ? true : params.status === 'UNREAD' ? false : undefined,
        exclude_cleared: params.exclude_cleared === true ? true : undefined,
        channel: params.channel || undefined,
      };
      const response = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.LIST, body);

      if (response.data?.success) {
        const resData = response.data;
        const items = Array.isArray(resData.data)
          ? resData.data
          : Array.isArray(resData.data?.data)
          ? resData.data.data
          : [];
        const total =
          resData.meta_data?.total_records ??
          resData.meta_data?.total ??
          resData.pagination?.total ??
          items.length;
        const unreadCount =
          resData.meta_data?.unread_count ??
          resData.pagination?.unread_count ??
          resData.data?.unread_count ??
          resData.unread_count;

        return {
          success: true,
          code: resData.code || 200,
          description: resData.description || '',
          data: items,
          meta_data: {
            total,
            skip: params.skip || 0,
            limit: params.limit || 20,
            unread_count: unreadCount ?? items.filter((n: any) => n.status !== 'READ' && n.is_read !== true).length,
          },
        };
      }

      return {
        success: false,
        code: 500,
        description: 'Failed to fetch notifications',
        data: [],
        meta_data: {
          total: 0,
          skip: params.skip || 0,
          limit: params.limit || 20,
          unread_count: 0,
        },
      };
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return {
        success: false,
        code: 500,
        description: 'Failed to fetch notifications',
        data: [],
        meta_data: {
          total: 0,
          skip: params.skip || 0,
          limit: params.limit || 20,
          unread_count: 0,
        },
      };
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await apiClient.get<UnreadCountResponse>(
        API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT
      );

      if (response.data?.success) {
        return response.data.data.unread_count;
      }

      return 0;
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      return 0;
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<NotificationActionResponse> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId)
      );

      if (response.data?.success) {
        return response.data;
      }

      return {
        success: false,
        code: 500,
        description: 'Failed to mark notification as read',
        data: {
          notification_id: notificationId,
          status: RecipientStatus.DELIVERED,
        },
      };
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return {
        success: false,
        code: 500,
        description: 'Failed to mark notification as read',
        data: {
          notification_id: notificationId,
          status: RecipientStatus.DELIVERED,
        },
      };
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ success: boolean; count: number }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);

      if (response.data?.success) {
        return {
          success: true,
          count: response.data.data?.count || 0,
        };
      }

      return { success: false, count: 0 };
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return { success: false, count: 0 };
    }
  }

  /**
   * Dismiss (delete) a notification
   */
  async dismissNotification(notificationId: string): Promise<boolean> {
    try {
      const response = await apiClient.delete(
        API_ENDPOINTS.NOTIFICATIONS.DISMISS(notificationId)
      );

      return response.data?.success || false;
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
      return false;
    }
  }

  /**
   * Get the bell popup feed — only unread + not-cleared items.
   * Dedicated endpoint so filter is enforced server-side. No pagination —
   * the set is naturally bounded to what the user hasn't seen/cleared.
   */
  async getBellFeed(): Promise<{ data: UserNotification[]; unread_count: number }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.BELL_FEED);
      if (response.data?.success) {
        const resData = response.data.data ?? {};
        const items = Array.isArray(resData.data) ? resData.data : [];
        const unread_count = resData.unread_count ?? items.length;
        return { data: items, unread_count };
      }
      return { data: [], unread_count: 0 };
    } catch (error) {
      console.error('Failed to fetch bell feed:', error);
      return { data: [], unread_count: 0 };
    }
  }

  /**
   * Mark notifications as toasted — prevents re-toasting on refresh/login.
   */
  async markAsToasted(notification_ids: string[]): Promise<boolean> {
    try {
      if (!notification_ids.length) return true;
      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.MARK_TOASTED,
        { notification_ids }
      );
      return response.data?.success || false;
    } catch (error) {
      console.error('Failed to mark notifications as toasted:', error);
      return false;
    }
  }

  /**
   * Clear a notification from the bell popup only.
   * The full My Notifications page still shows it. Also marks it as read
   * server-side so the unread badge is updated on the next fetch.
   */
  async clearFromPopup(notificationId: string): Promise<boolean> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.CLEAR_POPUP(notificationId)
      );
      return response.data?.success || false;
    } catch (error) {
      console.error('Failed to clear notification from popup:', error);
      return false;
    }
  }

  // ============== PREFERENCES ENDPOINTS ==============

  /**
   * Get user's notification preferences
   */
  async getPreferences(): Promise<NotificationPreferences | null> {
    try {
      const response = await apiClient.get<PreferencesResponse>(
        API_ENDPOINTS.NOTIFICATIONS.GET_PREFERENCES
      );

      if (response.data?.success) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
      return null;
    }
  }

  /**
   * Update user's notification preferences
   */
  async updatePreferences(
    preferences: UpdatePreferencesRequest
  ): Promise<NotificationPreferences | null> {
    try {
      const response = await apiClient.put<PreferencesResponse>(
        API_ENDPOINTS.NOTIFICATIONS.UPDATE_PREFERENCES,
        preferences
      );

      if (response.data?.success) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      return null;
    }
  }

  // ============== PUSH NOTIFICATION ENDPOINTS ==============

  /**
   * Get OneSignal push config for the current tenant.
   * Returns app_id if push is enabled, null otherwise.
   */
  async getPushConfig(): Promise<{ app_id: string; enabled: boolean; safari_web_id?: string } | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.PUSH_CONFIG);
      console.log('[PushConfig] API response:', { success: response.data?.success, data: response.data?.data });
      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }
      console.warn('[PushConfig] No push config data - push notifications disabled');
      return null;
    } catch (error) {
      console.error('[PushConfig] Failed to fetch push config:', error);
      return null;
    }
  }

  /**
   * Subscribe/unsubscribe to push notifications.
   * With external_id approach, frontend calls OneSignal.login(user_id)
   * and backend just tracks the enabled/disabled preference.
   */
  async subscribeToPush(enabled: boolean): Promise<boolean> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.PUSH_SUBSCRIBE, {
        enabled,
      });

      return response.data?.success || false;
    } catch (error) {
      console.error('Failed to update push subscription:', error);
      return false;
    }
  }

  // ============== ADMIN BELL (all notifications for admin dropdown) ==============

  /**
   * Get all notifications for admin bell dropdown.
   * Calls admin history API and maps to UserNotification shape for the dropdown.
   */
  async getAdminNotificationsForBell(params: NotificationQueryParams = {}): Promise<NotificationListResponse> {
    try {
      const page = params.skip != null ? Math.floor(params.skip / (params.limit || 20)) + 1 : 1;
      const limit = params.limit || 20;

      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.HISTORY,
        {
          page,
          limit,
          type: params.type || undefined,
        }
      );

      if (response.data?.success) {
        const resData = response.data;
        const history_items: NotificationHistoryItem[] =
          Array.isArray(resData.data) ? resData.data
          : Array.isArray(resData.data?.data) ? resData.data.data
          : [];
        const total =
          resData.meta_data?.total_records ??
          resData.meta_data?.total ??
          resData.pagination?.total ??
          history_items.length;

        // Map admin history items to UserNotification shape for the dropdown
        const mapped: UserNotification[] = history_items.map((item) => ({
          _id: item._id,
          title: item.title,
          message: item.message,
          type: item.type,
          priority: item.priority,
          action_url: null,
          context: null,
          status: RecipientStatus.READ, // Admin view: all shown as read (no unread badge needed)
          read_at: item.created_at,
          created_at: item.created_at,
        }));

        return {
          success: true,
          code: 200,
          description: 'Admin notifications fetched',
          data: mapped,
          meta_data: {
            total,
            skip: params.skip || 0,
            limit,
            unread_count: 0,
          },
        };
      }

      return {
        success: false,
        code: 500,
        description: 'Failed to fetch admin notifications',
        data: [],
        meta_data: { total: 0, skip: params.skip || 0, limit, unread_count: 0 },
      };
    } catch (error) {
      console.error('Failed to fetch admin notifications for bell:', error);
      return {
        success: false,
        code: 500,
        description: 'Failed to fetch admin notifications',
        data: [],
        meta_data: { total: 0, skip: params.skip || 0, limit: params.limit || 20, unread_count: 0 },
      };
    }
  }

  // ============== ADMIN ENDPOINTS ==============

  /**
   * Send a notification (admin only)
   */
  async sendNotification(
    notification: CreateNotificationRequest
  ): Promise<{ success: boolean; notification_id?: string; error?: string }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.SEND,
        notification
      );

      if (response.data?.success) {
        return {
          success: true,
          notification_id: response.data.data?.notification_id,
        };
      }

      return {
        success: false,
        error: extractErrorMessage(response.data) || 'Failed to send notification',
      };
    } catch (error: any) {
      console.error('Failed to send notification:', error);
      return {
        success: false,
        error: extractErrorMessage(error.response?.data) || 'Failed to send notification',
      };
    }
  }

  /**
   * Schedule a notification (admin only)
   */
  async scheduleNotification(
    notification: ScheduleNotificationRequest
  ): Promise<{ success: boolean; notification_id?: string; error?: string }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.SCHEDULE,
        notification
      );

      if (response.data?.success) {
        return {
          success: true,
          notification_id: response.data.data?.notification_id,
        };
      }

      return {
        success: false,
        error: extractErrorMessage(response.data) || 'Failed to schedule notification',
      };
    } catch (error: any) {
      console.error('Failed to schedule notification:', error);
      return {
        success: false,
        error: extractErrorMessage(error.response?.data) || 'Failed to schedule notification',
      };
    }
  }

  /**
   * Get notification history (admin only)
   */
  async getNotificationHistory(params: {
    page?: number;
    limit?: number;
    type?: string;
    search?: string;
    history_status?: 'SENT' | 'SCHEDULED' | 'CANCELLED' | 'FAILED';
    start_date?: string;
    end_date?: string;
  } = {}): Promise<NotificationHistoryResponse> {
    try {
      const page = params.page || 1;
      const limit = params.limit || 20;

      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.HISTORY,
        {
          page,
          limit,
          type: params.type || undefined,
          search: params.search || undefined,
          history_status: params.history_status || undefined,
          start_date: params.start_date || undefined,
          end_date: params.end_date || undefined,
        }
      );

      if (response.data?.success) {
        const resData = response.data;
        const items = Array.isArray(resData.data)
          ? resData.data
          : Array.isArray(resData.data?.data)
          ? resData.data.data
          : [];
        const total =
          resData.meta_data?.total_records ??
          resData.meta_data?.total ??
          resData.pagination?.total ??
          items.length;

        return {
          success: true,
          code: resData.code || 200,
          data: items,
          meta_data: {
            total,
            skip: (page - 1) * limit,
            limit,
          },
        };
      }

      return {
        success: false,
        code: 500,
        data: [],
        meta_data: {
          total: 0,
          skip: 0,
          limit,
        },
      };
    } catch (error) {
      console.error('Failed to fetch notification history:', error);
      return {
        success: false,
        code: 500,
        data: [],
        meta_data: {
          total: 0,
          skip: 0,
          limit: params.limit || 20,
        },
      };
    }
  }

  /**
   * Cancel a scheduled notification (admin only)
   */
  async cancelScheduledNotification(notificationId: string): Promise<boolean> {
    try {
      const response = await apiClient.delete(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.CANCEL(notificationId)
      );

      return response.data?.success || false;
    } catch (error) {
      console.error('Failed to cancel scheduled notification:', error);
      return false;
    }
  }

  async retryFailedNotification(notificationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.RETRY(notificationId)
      );

      return { success: response.data?.success || false };
    } catch (error: any) {
      console.error('Failed to retry notification:', error);
      return { success: false, error: error?.response?.data?.description || 'Retry failed' };
    }
  }

  /**
   * Get notification metrics (admin only)
   */
  async getNotificationMetrics(): Promise<{
    success: boolean;
    data: {
      total_created: number;
      by_channel: Record<string, number>;
      sent_count: number;
      scheduled_count: number;
      cancelled_count: number;
      failed_count: number;
    } | null;
  }> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.ADMIN.METRICS);

      if (response.data?.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return { success: false, data: null };
    } catch (error) {
      console.error('Failed to fetch notification metrics:', error);
      return { success: false, data: null };
    }
  }

  /**
   * Get recipients for a specific notification with user details (admin)
   */
  async getNotificationRecipients(notification_id: string): Promise<NotificationRecipientsResponse | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.ADMIN.RECIPIENTS(notification_id));
      const data = response.data?.data || response.data;
      return data || null;
    } catch (error) {
      console.error('Failed to fetch notification recipients:', error);
      return null;
    }
  }

  /**
   * List push subscribers for the tenant (queries OneSignal directly via backend).
   * Throws on failure so React Query's isError state can render an error UI —
   * silently returning `[]` made OneSignal outages indistinguishable from a
   * genuinely empty list.
   */
  async getPushSubscribers(options?: { limit?: number; offset?: number }): Promise<PushSubscriberListResponse> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.PUSH_SUBSCRIBERS,
        { limit: options?.limit ?? 20, offset: options?.offset ?? 0 },
      );
      const data = response.data?.data || response.data;
      return {
        subscribers: data?.subscribers || [],
        total_count: data?.total_count,
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.error_description ||
        error?.message ||
        'Failed to load subscribers';
      console.error('Failed to fetch push subscribers:', message);
      throw new Error(message);
    }
  }

  /**
   * Admin force-unsubscribe a user from push notifications (deletes on OneSignal + sets local preference to false)
   */
  async adminUnsubscribeUser(user_id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.NOTIFICATIONS.ADMIN.PUSH_UNSUBSCRIBE(user_id));
      return { success: response.data?.success === true };
    } catch (error: any) {
      const message = error?.response?.data?.error_description || error?.message || 'Unsubscribe failed';
      console.error('Failed to unsubscribe user:', message);
      return { success: false, error: message };
    }
  }

  /**
   * Sync push subscription state from OneSignal to our DB.
   * Called after login — if OneSignal says the user is no longer subscribed,
   * backend flips channels.push.enabled=false so the Settings toggle
   * reflects reality. Non-blocking, fail-safe.
   */
  async syncPushState(): Promise<{ synced: boolean; subscribed: boolean; flag_updated: boolean } | null> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.PUSH_SYNC_STATE);
      const data = response.data?.data || response.data;
      return data || null;
    } catch (error) {
      // Fail-safe: if sync fails, don't block login — just log
      console.warn('[Notifications] Push state sync failed:', error);
      return null;
    }
  }

  /**
   * Admin unsubscribe a single device/session by its OneSignal player_id.
   * Does NOT affect the user's other sessions.
   */
  async adminUnsubscribeSubscription(player_id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiClient.delete(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.PUSH_UNSUBSCRIBE_SUBSCRIPTION(player_id)
      );
      return { success: response.data?.success === true };
    } catch (error: any) {
      const message = error?.response?.data?.error_description || error?.message || 'Unsubscribe failed';
      console.error('Failed to unsubscribe subscription:', message);
      return { success: false, error: message };
    }
  }

  // ============== SYSTEM NOTIFICATION CONFIGURATION ==============

  /**
   * Get every configurable operation, grouped by module in catalog order
   */
  async getSystemNotificationConfigs(
    module?: SystemNotificationModule
  ): Promise<SystemNotificationConfigList | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.ADMIN.SYSTEM_CONFIG_LIST, {
        params: module ? { module } : undefined,
      });

      if (response.data?.success) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Failed to fetch system notification configs:', error);
      return null;
    }
  }

  /**
   * Save the configuration for a single operation
   */
  async upsertSystemNotificationConfig(
    event_type: string,
    payload: UpsertSystemNotificationConfigRequest
  ): Promise<{ success: boolean; data?: SystemNotificationConfigItem; error?: string }> {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.NOTIFICATIONS.ADMIN.SYSTEM_CONFIG_EVENT(event_type),
        payload
      );

      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }

      return { success: false, error: extractErrorMessage(response.data) || 'Save failed' };
    } catch (error: any) {
      const message = extractErrorMessage(error?.response?.data) || error?.message || 'Save failed';
      console.error('Failed to save system notification config:', message);
      return { success: false, error: message };
    }
  }

  /**
   * Save several operations in one request
   */
  async bulkUpsertSystemNotificationConfigs(
    items: Array<UpsertSystemNotificationConfigRequest & { event_type: string }>
  ): Promise<{ success: boolean; data?: SystemNotificationConfigList; error?: string }> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.NOTIFICATIONS.ADMIN.SYSTEM_CONFIG_BULK, {
        items,
      });

      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }

      return { success: false, error: extractErrorMessage(response.data) || 'Save failed' };
    } catch (error: any) {
      const message = extractErrorMessage(error?.response?.data) || error?.message || 'Save failed';
      console.error('Failed to bulk save system notification configs:', message);
      return { success: false, error: message };
    }
  }
}

export const notificationService = new NotificationService();
